/**
 * Sentry Configuration with Performance Monitoring
 *
 * This file initializes Sentry with performance tracing and should be
 * imported FIRST in the application entry point (app.jsx) before any
 * other code runs to ensure all errors and performance data are captured.
 *
 * Environment-based configuration:
 * - prod: 50% traces (API calls only), 10% profiles, 1% replays
 * - stg: 50% traces (API calls only), 50% profiles, 5% replays
 * - local/dev: Disabled (no data sent to Sentry)
 */

var Sentry = require('@sentry/react');
var env = require('env');
var {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} = require('react-router-dom');
var React = require('react');

var isProd = env.stage === 'prod';
var isStaging = env.stage === 'stg';
// Only enable Sentry in production and staging environments
var isEnabled = isProd || isStaging;

/**
 * Transform HTTP span descriptions to be more readable
 * e.g., "GET https://xxx.execute-api.../stg/notification/123" → "GET stg:/notification/:id"
 */
var formatHttpSpanDescription = (url) => {
  try {
    var parsedUrl = new URL(url);
    var pathname = parsedUrl.pathname;
    var search = parsedUrl.search;

    // Extract environment from path (e.g., /stg/, /prod/, /dev/)
    var envMatch = pathname.match(/^\/(stg|prod|dev)\//);
    var apiEnv = envMatch ? envMatch[1] : '';

    // Remove the env prefix from path
    var cleanPath = envMatch
      ? pathname.replace(/^\/(stg|prod|dev)/, '')
      : pathname;

    // Replace numeric IDs with :id placeholder for grouping
    // Matches UUIDs, numeric IDs, and alphanumeric IDs (8+ chars)
    var parameterizedPath = cleanPath
      .split('/')
      .map((segment) => {
        // UUID pattern
        if (
          /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            segment,
          )
        ) {
          return ':id';
        }
        // Pure numeric ID
        if (/^\d+$/.test(segment) && segment.length > 0) {
          return ':id';
        }
        // Long alphanumeric ID (8+ chars, mix of letters and numbers)
        if (
          /^[a-z0-9]{8,}$/i.test(segment) &&
          /\d/.test(segment) &&
          /[a-z]/i.test(segment)
        ) {
          return ':id';
        }
        return segment;
      })
      .join('/');

    return apiEnv + ':' + parameterizedPath + search;
  } catch (e) {
    // Log parsing errors to console (not Sentry to avoid recursion)
    console.warn('Failed to format HTTP span URL:', e.message);
    return url;
  }
};

if (isEnabled) {
  Sentry.init({
    dsn: 'https://a706d7c7ac2f4dc28d7e98c6389c1930@sentry.io/1390702',
    release: env.stage + '_' + env.version,
    environment: env.stage,

    // Only enable in stg/prod environments
    enabled: true,

    // Performance monitoring - environment-based sampling
    // Temporarily set to 100% for debugging, reduce to 0.5 for production
    tracesSampleRate: 1.0,
    profilesSampleRate: isProd ? 0.1 : 0.5,

    // Privacy - don't send personally identifiable information by default
    sendDefaultPii: false,

    // Only propagate trace headers to our backend API. Lives at the
    // top level in v8+ — used to live on the BrowserTracing integration
    // in v7. Prevents CORS errors with third-party services
    // (Mixpanel, etc.) by skipping `sentry-trace` / `baggage` headers.
    tracePropagationTargets: [/\.execute-api\./, /localhost:3000/],

    // Integrations for performance tracing
    integrations: [
      // Browser profiling — needed for `profilesSampleRate` to actually
      // collect anything. Requires the `Document-Policy: js-profiling`
      // response header to be set by the server (otherwise the
      // browser's Profiler API is blocked and the integration silently
      // no-ops).
      Sentry.browserProfilingIntegration(),
      // Drop errors whose stack frames are *exclusively* third-party
      // (browser extensions, injected scripts). The webpack plugin
      // tags our bundles with `applicationKey: 'sciroot-webapp'` so
      // Sentry knows which frames belong to us.
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['sciroot-webapp'],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
      // React Router integration for proper route-based transaction names
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation: useLocation,
        useNavigationType: useNavigationType,
        createRoutesFromChildren: createRoutesFromChildren,
        matchRoutes: matchRoutes,
        // Enable page load tracing to create transactions for HTTP spans
        instrumentPageLoad: true,
        instrumentNavigation: false,
        // Disable INP (Interaction to Next Paint) capture — was
        // `enableInteractions` in v7, renamed in v8+.
        enableInp: false,
        // Only trace requests to our backend API (filter out third-party services)
        shouldCreateSpanForRequest: (url) => {
          // Get the configured API URL for current environment
          var apiUrl = env[env.stage]?.url || '';
          // Check if request is to our backend (handles all environments and regions)
          if (apiUrl && url.startsWith(apiUrl)) {
            return true;
          }
          // Fallback patterns for AWS API Gateway and local development
          return (
            url.includes('.execute-api.') || url.includes('localhost:3000')
          );
        },
      }),
      // Optional: Session replay for debugging (1% in prod, 5% in staging)
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session replay sample rates
    replaysSessionSampleRate: isProd ? 0.01 : 0.05,
    replaysOnErrorSampleRate: isProd ? 0.1 : 0.5,

    // Filter out common non-actionable errors
    ignoreErrors: [
      // Network errors that are expected in some cases
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      // Browser extension errors
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
    ],

    // Transform HTTP span descriptions for better grouping
    beforeSendSpan: (span) => {
      // Check if this is an HTTP span
      if (
        span.op === 'http.client' ||
        span.op === 'fetch' ||
        span.op === 'xhr'
      ) {
        var description = span.description || '';
        // Format: "GET https://..." or "POST https://..."
        var match = description.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(.+)$/);
        if (match) {
          var method = match[1];
          var url = match[2];
          span.description = method + ' ' + formatHttpSpanDescription(url);
        }
      }
      return span;
    },
  });
}

// Export Sentry and the HOC for wrapping Routes
module.exports = {
  Sentry: Sentry,
  isEnabled: isEnabled,
  // HOC to wrap Routes component for route change tracking
  withSentryRouting: Sentry.withSentryReactRouterV6Routing,
};
